import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Button, Form, Spinner } from 'react-bootstrap';
import { PencilFill, CheckLg, XLg, PlusCircle, Trash } from 'react-bootstrap-icons';
import { useAuth } from '../../contexts/SecurityContext';
import RoleGuard from '../security/RoleGuard';
import SectionControls from '../SectionControls';
import { updateFaqSection } from '../../services/contentService';
import './FaqBlock.scss';

function defaultFaq() {
  return { question: 'Question goes here?', answer: 'Short answer paragraph.' };
}

export function defaultFaqData() {
  return {
    title: 'Frequently asked',
    faqs: [defaultFaq(), defaultFaq(), defaultFaq()],
  };
}

export function FaqView({ data }) {
  const faqs = Array.isArray(data?.faqs) ? data.faqs : [];
  return (
    <div className="faq-block">
      {data?.title ? <h2 className="faq-block__title">{data.title}</h2> : null}
      <div className="faq-block__list">
        {faqs.map((faq, idx) => (
          <div key={idx} className="faq-block__item">
            <div className="faq-block__question">{faq.question}</div>
            <div className="faq-block__answer">{faq.answer}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FaqBlock({
  pageId, section, onMoveUp, onMoveDown, onDelete, isFirstSection, isLastSection,
}) {
  const { user } = useAuth();
  const initialData = useMemo(() => section?.data || defaultFaqData(), [section]);
  const [data, setData] = useState(initialData);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => { setData(section?.data || defaultFaqData()); }, [section]);

  async function handleSave() {
    setIsSaving(true);
    try {
      const modifiedBy = user?.attributes?.email || user?.username || 'user';
      await updateFaqSection(pageId, section.position, data, modifiedBy);
      setIsEditing(false);
    } catch (e) {
      console.error('Error saving FAQ:', e);
      // eslint-disable-next-line no-alert
      alert('Failed to save section.');
    } finally {
      setIsSaving(false);
    }
  }

  function handleCancel() { setData(section?.data || defaultFaqData()); setIsEditing(false); }
  function updateField(field, value) { setData((prev) => ({ ...prev, [field]: value })); }
  function updateFaq(idx, patch) {
    setData((prev) => {
      const next = { ...prev, faqs: [...(prev.faqs || [])] };
      next.faqs[idx] = { ...next.faqs[idx], ...patch };
      return next;
    });
  }
  function addFaq() { setData((prev) => ({ ...prev, faqs: [...(prev.faqs || []), defaultFaq()] })); }
  function removeFaq(idx) {
    setData((prev) => {
      const next = { ...prev, faqs: [...(prev.faqs || [])] };
      next.faqs.splice(idx, 1);
      return next;
    });
  }

  if (isEditing) {
    return (
      <div className="faq-block faq-block--editing">
        <Form.Group className="mb-3">
          <Form.Label>Section title</Form.Label>
          <Form.Control type="text" value={data.title || ''} onChange={(e) => updateField('title', e.target.value)} />
        </Form.Group>
        {(data.faqs || []).map((faq, idx) => (
          <div key={idx} className="faq-block__editor-item">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>FAQ {idx + 1}</strong>
              <Button variant="outline-danger" size="sm" onClick={() => removeFaq(idx)}>
                <Trash /> Remove
              </Button>
            </div>
            <Row className="g-2">
              <Col md={12}>
                <Form.Label>Question</Form.Label>
                <Form.Control type="text" value={faq.question || ''} onChange={(e) => updateFaq(idx, { question: e.target.value })} />
              </Col>
              <Col md={12}>
                <Form.Label>Answer</Form.Label>
                <Form.Control as="textarea" rows={2} value={faq.answer || ''} onChange={(e) => updateFaq(idx, { answer: e.target.value })} />
              </Col>
            </Row>
          </div>
        ))}
        <Button variant="outline-primary" size="sm" onClick={addFaq} className="mb-3">
          <PlusCircle /> Add FAQ
        </Button>
        <div className="faq-block__editor-actions">
          <Button variant="success" onClick={handleSave} disabled={isSaving}>
            {isSaving ? (<><Spinner as="span" animation="border" size="sm" className="me-2" />Saving…</>) : (<><CheckLg /> Save</>)}
          </Button>
          <Button variant="secondary" onClick={handleCancel} disabled={isSaving}><XLg /> Cancel</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="faq-block faq-block--view">
      <FaqView data={data} />
      <RoleGuard role="admin">
        <div className="admin-controls">
          <Button variant="outline-secondary" size="sm" className="edit-btn" onClick={() => setIsEditing(true)}>
            <PencilFill /> Edit FAQ
          </Button>
          <SectionControls onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDelete={onDelete} isFirstSection={isFirstSection} isLastSection={isLastSection} />
        </div>
      </RoleGuard>
    </div>
  );
}

export default FaqBlock;
